import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@lib/api';

const Applications: React.FC = () => {
  const navigate = useNavigate();
  const [showNewForm, setShowNewForm] = useState(false);
  const [newApp, setNewApp] = useState({ app_id: '', display_name: '', category: 'integration', description: '' });

  const { data: appsData, refetch } = useQuery({
    queryKey: ['apps-list'],
    queryFn: async () => {
      const response = await apiClient.get<any>('/api/apps?limit=100');
      return response;
    },
  });

  const handleCreateApp = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiClient.post('/api/apps', newApp);
      setNewApp({ app_id: '', display_name: '', category: 'integration', description: '' });
      setShowNewForm(false);
      refetch();
    } catch (error) {
      console.error('Failed to create app:', error);
    }
  };

  const apps = appsData?.items || [];

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Applications</h1>
        <button
          onClick={() => setShowNewForm(!showNewForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          New Application
        </button>
      </div>

      {showNewForm && (
        <div className="bg-white rounded-lg p-6 border border-gray-200 mb-6">
          <h2 className="text-lg font-semibold mb-4">Create New Application</h2>
          <form onSubmit={handleCreateApp} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">App ID</label>
              <input
                type="text"
                value={newApp.app_id}
                onChange={(e) => setNewApp({ ...newApp, app_id: e.target.value })}
                placeholder="e.g., wrike_addon"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
              <input
                type="text"
                value={newApp.display_name}
                onChange={(e) => setNewApp({ ...newApp, display_name: e.target.value })}
                placeholder="e.g., Wrike Integration"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={newApp.category}
                onChange={(e) => setNewApp({ ...newApp, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="integration">Integration</option>
                <option value="harvesting">Harvesting</option>
                <option value="ai">AI</option>
                <option value="compliance">Compliance</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={newApp.description}
                onChange={(e) => setNewApp({ ...newApp, description: e.target.value })}
                placeholder="Brief description of this application"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create
              </button>
              <button
                type="button"
                onClick={() => setShowNewForm(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">App ID</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Display Name</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Category</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
            </tr>
          </thead>
          <tbody>
            {apps.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                  No applications yet. Create one to get started.
                </td>
              </tr>
            ) : (
              apps.map((app: any) => (
                <tr
                  key={app.app_id}
                  className="border-b border-gray-200 hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/applications/${app.app_id}`)}
                >
                  <td className="px-6 py-4 text-sm font-medium text-blue-600 underline">{app.app_id}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{app.display_name}</td>
                  <td className="px-6 py-4 text-sm">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                      {app.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{app.description || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Applications;
